import { useEffect, useRef } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

const tourKey = (appId) => `glos_tour_done_v1_${appId}`;

// Steps shown to all users (members)
const BASE_STEPS = [
  {
    element: '#tour-feed',
    popover: {
      title: '📣 Feed',
      description: 'Stay updated with your team. See announcements, appreciation, and what\'s happening across the workspace.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '#tour-chat',
    popover: {
      title: '💬 Chat',
      description: 'Message teammates directly or start group conversations without leaving GLOS.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '#tour-tasks',
    popover: {
      title: '✅ Tasks',
      description: 'View tasks assigned to you, track progress, and log your time.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '#tour-projects',
    popover: {
      title: '📁 Projects',
      description: 'See projects you\'re part of and the tasks linked to them.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '#tour-reports',
    popover: {
      title: '📊 Reports',
      description: 'Track your own performance, tasks, and contributions over time.',
      side: 'right',
      align: 'start',
    },
  },
];

// Profile step — shown last; description varies by role
const profileStep = (isAdmin) => ({
  element: '#tour-profile',
  popover: {
    title: '👤 Profile & Billing',
    description: isAdmin
      ? 'Open this menu for Account & Billing, Organisation Setup and Users — invite your team, set up branches and departments, and upgrade your plan anytime.'
      : 'Complete your profile so your teammates know who you are.',
    side: 'bottom',
    align: 'end',
  },
});

// Admin-only steps — shown first, before base steps
const ADMIN_STEPS = [
  {
    element: '#tour-users',
    popover: {
      title: '👥 Users',
      description: 'Start here — invite your team by email and assign each person a role. Your workspace comes alive when your people are in.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '#tour-roles',
    popover: {
      title: '🔑 Roles',
      description: 'Create roles that match your company structure. Roles control what each person can see and do across the workspace.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '#tour-organisation',
    popover: {
      title: '🏢 Organisation',
      description: 'Set up your branches and departments. This becomes the backbone of how your team is organised in GLOS.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '#tour-clients',
    popover: {
      title: '🤝 Clients',
      description: 'Add the clients you work with. You\'ll need them to create agreements and track services.',
      side: 'right',
      align: 'start',
    },
  },
];

// Admin feed/chat/tasks/projects/reports with fuller descriptions
const ADMIN_BASE_STEPS = [
  {
    element: '#tour-feed',
    popover: {
      title: '📣 Feed',
      description: 'Your team\'s social space — post announcements, celebrate wins, share appreciation and feedback.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '#tour-chat',
    popover: {
      title: '💬 Chat',
      description: 'Message teammates one-on-one or in groups without leaving GLOS.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '#tour-tasks',
    popover: {
      title: '✅ Tasks',
      description: 'Create tasks, assign them to your team, set deadlines and priorities. Everything that needs to get done lives here.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '#tour-projects',
    popover: {
      title: '📁 Projects',
      description: 'Group related tasks under a project. Track timelines, ownership, and progress all in one place.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '#tour-reports',
    popover: {
      title: '📊 Reports',
      description: 'Once your team is active, get insights on tasks, projects, financials, and more.',
      side: 'right',
      align: 'start',
    },
  },
];

function buildDriver(isAdmin, appId) {
  // Admin order:  Users → Roles → Organisation → Clients → Feed → Chat → Tasks → Projects → Reports → Profile & Billing
  // Member order: Feed → Chat → Tasks → Projects → Reports → Profile & Billing
  const allSteps = isAdmin
    ? [...ADMIN_STEPS, ...ADMIN_BASE_STEPS, profileStep(true)]
    : [...BASE_STEPS, profileStep(false)];

  const validSteps = allSteps.filter(s => document.querySelector(s.element));
  if (validSteps.length === 0) return null;

  const markDone = () => {
    try { localStorage.setItem(tourKey(appId), '1'); } catch {}
  };

  const onHighlighted = (el) => {
    // Inject skip button
    document.querySelectorAll('.glos-skip-btn').forEach(e => e.remove());
    const closeBtn = document.querySelector('.driver-popover-close-btn');
    if (closeBtn) closeBtn.style.display = 'none';
    const footer = document.querySelector('.driver-popover-footer');
    if (footer) {
      const btn = document.createElement('button');
      btn.className = 'glos-skip-btn';
      btn.textContent = 'Skip';
      btn.onclick = () => { markDone(); d.destroy(); };
      footer.appendChild(btn);
    }

    // Clamp stage SVG cutout to the sidebar's actual width so it never overflows
    const sidebar = document.querySelector('.workspace-sidebar');
    const stageEl = document.querySelector('.driver-stage');
    if (sidebar && stageEl && el) {
      const sidebarRect = sidebar.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      // Only clamp if the highlighted element is inside the sidebar
      if (elRect.left >= sidebarRect.left && elRect.right <= sidebarRect.right + 10) {
        stageEl.style.maxWidth = sidebarRect.width + 'px';
        stageEl.style.left = sidebarRect.left + 'px';
        stageEl.style.width = sidebarRect.width + 'px';
      } else {
        stageEl.style.maxWidth = '';
        stageEl.style.left = '';
        stageEl.style.width = '';
      }
    }
  };

  const d = driver({
    animate: true,
    smoothScroll: true,
    showProgress: true,
    allowClose: false,
    showButtons: ['next', 'previous'],
    progressText: '{{current}} of {{total}}',
    nextBtnText: 'Next →',
    prevBtnText: '← Back',
    doneBtnText: 'Done 🎉',
    overlayColor: 'rgba(0,0,0,0.55)',
    stagePadding: 0,
    stageRadius: 4,
    popoverClass: 'glos-tour-popover',
    onHighlighted: onHighlighted,
    onDestroyStarted: () => {
      markDone();
      d.destroy();
    },
    steps: validSteps,
  });

  return d;
}

export default function AppTour({ isAdmin, appId }) {
  const driverRef = useRef(null);

  useEffect(() => {
    if (!appId) return;
    try {
      if (localStorage.getItem(tourKey(appId))) return;
    } catch { return; }

    const timer = setTimeout(() => {
      driverRef.current = buildDriver(isAdmin, appId);
      driverRef.current?.drive();
    }, 900);

    return () => {
      clearTimeout(timer);
      driverRef.current?.destroy();
    };
  }, [isAdmin, appId]);

  return null;
}
